import React, { useState } from "react";
import { Box, TextField } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import PrimaryButton from "../../../widgets/Buttons/PrimaryButton";
import SecondaryButton from "../../../widgets/Buttons/SecondaryButton";
import ActionCard from "../../../widgets/ActionCard/ActionCard";
import CustomModal from "../../../widgets/Modal/CustomModal";
import CustomTable from "../../../widgets/Table/CustomTable";
import ErrorSnackbar from "../../../widgets/Snackbar/ErrorSnackbar";
import SuccessSnackbar from "../../../widgets/Snackbar/SuccessSnackbar";
import { useGetCallLogs } from "../../../hooks/useGetCallLogs";
import { useUpsertNotes } from "../../../hooks/useUpsertNotes";

const CallManagementCard = ({ uamId , adminId}) => {
  const [isCallLogsModalOpen, setIsCallLogsModalOpen] = useState(false);
  const [viewNotesText, setViewNotesText] = useState("");
  const [viewUrlText, setViewUrlText] = useState("");
  const [notesModal, setNotesModal] = useState({
    open: false,
    text: "",
    callId: "",
  });
  const [errorSnackbar, setErrorSnackbar] = useState({
    open: false,
    message: "",
  });
  const [successSnackbar, setSuccessSnackbar] = useState({
    open: false,
    message: "",
  });

  const {
    data: callLogs,
    isLoading: isLoadingLogs,
  } = useGetCallLogs(uamId, adminId, isCallLogsModalOpen);

  const {
    mutate: upsertNotes,
    isPending: isUpsertingNotesPending,
    isLoading: isUpsertingNotesLoading,
  } = useUpsertNotes();
  const isUpsertingNotes = isUpsertingNotesPending || isUpsertingNotesLoading;

  const handleUpsertNotes = () => {
    upsertNotes(
      { call_id: notesModal.callId, notes: notesModal.text },
      {
        onSuccess: () => {
          setSuccessSnackbar({
            open: true,
            message: "Notes saved successfully.",
          });
          setNotesModal({ open: false, text: "", callId: "" });
        },
        onError: (err) => {
          setErrorSnackbar({
            open: true,
            message: err.message || "Failed to save notes.",
          });
        },
      },
    );
  };

  const callLogColumns = [
    {
      id: "call_id",
      label: "Call ID",
      cellSx: { fontWeight: 600 },
      render: (row) => row.callId || row.call_id || "—",
    },
    {
      id: "start_time",
      label: "Start Time",
      render: (row) => {
        const time = row.startTime || row.start_time;
        return time ? new Date(time).toLocaleString() : "—";
      },
    },
    {
      id: "end_time",
      label: "End Time",
      render: (row) => {
        const time = row.endTime || row.end_time;
        return time ? new Date(time).toLocaleString() : "—";
      },
    },
    {
      id: "status_id",
      label: "Status ID",
      cellSx: { fontWeight: 600 },
      render: (row) => row.statusId || row.status_id || "—",
    },
    {
      id: "notes",
      label: "Notes",
      render: (row) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <SecondaryButton
            sx={{
              height: "30px",
              fontSize: "0.75rem",
              minWidth: "60px",
              px: 1,
            }}
            onClick={() => setViewNotesText(row.notes)}
            disabled={!row.notes}
          >
            View
          </SecondaryButton>
        </Box>
      ),
    },
    {
      id: "recording_url",
      label: "Recording",
      render: (row) => {
        const url = row.recordingUrl || row.recording_url;
        return (
          <SecondaryButton
            sx={{
              height: "30px",
              fontSize: "0.75rem",
              minWidth: "60px",
              px: 1,
            }}
            onClick={() => setViewUrlText(url)}
            disabled={!url}
          >
            View
          </SecondaryButton>
        );
      },
    },
  ];

  return (
    <>
      <ActionCard title="Call Management">
        <PrimaryButton
          startIcon={<HistoryIcon />}
          onClick={() => setIsCallLogsModalOpen(true)}
          sx={{ height: "35px" }}
        >
          View Call Logs
        </PrimaryButton>
      </ActionCard>

      <CustomModal
        open={isCallLogsModalOpen}
        onClose={() => setIsCallLogsModalOpen(false)}
        title="Call Logs"
        maxWidth="md"
        actions={
          <SecondaryButton onClick={() => setIsCallLogsModalOpen(false)}>
            Close
          </SecondaryButton>
        }
      >
        <CustomTable
          columns={callLogColumns}
          data={callLogs?.data || callLogs?.items || []}
          isLoading={isLoadingLogs}
          noDataMessage="No call logs found."
          sx={{ border: "none" }}
        />
      </CustomModal>

      <CustomModal
        open={!!viewNotesText}
        onClose={() => setViewNotesText("")}
        title="View Call Notes"
        actions={
          <SecondaryButton onClick={() => setViewNotesText("")}>
            Close
          </SecondaryButton>
        }
      >
        <Box
          sx={{
            p: 2,
            bgcolor: "#f8fafc",
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            whiteSpace: "pre-wrap",
            minHeight: "100px",
          }}
        >
          {viewNotesText}
        </Box>
      </CustomModal>

      <CustomModal
        open={notesModal.open}
        onClose={() => setNotesModal({ open: false, text: "", callId: "" })}
        title="Edit Call Notes"
        actions={
          <>
            <SecondaryButton
              onClick={() =>
                setNotesModal({ open: false, text: "", callId: "" })
              }
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleUpsertNotes}
              isLoading={isUpsertingNotes}
            >
              Save
            </PrimaryButton>
          </>
        }
      >
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Enter notes..."
            value={notesModal.text}
            slotProps={{ htmlInput: { maxLength: 200 } , formHelperText: { sx: { textAlign: "right", marginRight: 0 } }}}
            onChange={(e) =>
              setNotesModal((prev) => ({ ...prev, text: e.target.value }))
            }
            helperText={`${notesModal.text.length}/200 characters`}
          />
        </Box>
      </CustomModal>

      <CustomModal
        open={!!viewUrlText}
        onClose={() => setViewUrlText("")}
        title="Recording URL"
        actions={
          <SecondaryButton onClick={() => setViewUrlText("")}>
            Close
          </SecondaryButton>
        }
      >
        <Box
          sx={{
            p: 2,
            bgcolor: "#f8fafc",
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            wordBreak: "break-all",
          }}
        >
          <a href={viewUrlText} target="_blank" rel="noopener noreferrer">
            {viewUrlText}
          </a>
        </Box>
      </CustomModal>

      <ErrorSnackbar
        open={errorSnackbar.open}
        message={errorSnackbar.message}
        onClose={() => setErrorSnackbar({ open: false, message: "" })}
      />
      <SuccessSnackbar
        open={successSnackbar.open}
        message={successSnackbar.message}
        onClose={() => setSuccessSnackbar({ open: false, message: "" })}
      />
    </>
  );
};

export default CallManagementCard;
